import { c } from 'ttag';

import { refusalCode } from '../../graphql/refusalCode';
import { Refusal } from '../accounts/portalAccess';

/**
 * Reading of the refusals of the six partner-list mutations: PCG-276.
 *
 * The **seventh** vocabulary of the back-office, and it reuses `refusalCode` and
 * `Refusal` rather than restating them: how a business code travels on an Apollo error is
 * a property of the API, not of a screen.
 *
 * **Attention, this file was carved out of `catalogues/refusals.ts`, and that is not
 * duplication.** That file served both catalogues through a `Catalogue` discriminator
 * whose only reason to exist was `MISSING_PERMISSION`, which names `manage_accounts` for
 * a dashboard and `manage_referential` for a list. One function for two audiences forced
 * compromise wording everywhere else too: « for a dashboard it is the label the client
 * reads; for a partner list it is the theme itself » is a sentence written for nobody.
 * Separated, each side says one thing.
 *
 * **Attention, never « réessayez » unless retrying is genuinely the fix.** Three codes
 * below answer a second click identically: `PARTNER_LIST_NAME_TAKEN`, because an archived
 * list keeps its name for ever; `PARTNER_LIST_ARCHIVED`, because nothing here unarchives;
 * and `PARTNER_LIST_HAS_HOLDERS`, which needs a gesture on another screen.
 */

const refusalFor = (code: string | null): Refusal | null => {
    switch (code) {
        case 'NOT_AUTHENTICATED':
            return {
                title: c('Partner list refusal').t`Your session no longer carries an active membership.`,
                guidance: c('Partner list refusal').t`Sign in again, then start over.`,
            };
        case 'INTERNAL_STAFF_ONLY':
            return {
                title: c('Partner list refusal').t`This gesture belongs to the back-office.`,
                guidance: c('Partner list refusal')
                    .t`A partner list is a platform referential shared by every client, so a client-side role cannot curate it whatever its permissions. The scope of the role is checked before the permission, which is why a custom role holding the right is refused too.`,
            };
        case 'MISSING_PERMISSION':
            return {
                title: c('Partner list refusal').t`Your role does not let you manage the referential.`,
                guidance: c('Partner list refusal')
                    .t`Only a back-office administrator curates the partner lists. Managing accounts is not enough here: the two rights are deliberately distinct, and this one is the narrower. Ask an administrator for it, or ask them to make the change.`,
            };
        case 'EMPTY_CATALOGUE_NAME':
            return {
                title: c('Partner list refusal').t`A list carries a name, and that name is its theme.`,
                guidance: c('Partner list refusal')
                    .t`« Partenaires IA », « Cybersécurité »: what the list is about. Type one and send again.`,
            };
        case 'PARTNER_LIST_NAME_TAKEN':
            return {
                title: c('Partner list refusal').t`A list already carries this name, and it may be an archived one.`,
                guidance: c('Partner list refusal')
                    .t`The name of a partner list is unique across the whole platform, archived lists included: archiving withdraws a list from the catalogue but keeps its name reserved for ever. So this is not a temporary clash and clicking again will refuse again — choose another name. There is no screen that lists the archived ones, so the old name cannot be freed from here.`,
            };
        case 'PARTNER_LIST_NOT_FOUND':
            return {
                title: c('Partner list refusal').t`This list no longer exists.`,
                guidance: c('Partner list refusal')
                    .t`Somebody has probably archived it since this screen was loaded. Reload the page to see the catalogue as it is now.`,
            };
        case 'PARTNER_LIST_ARCHIVED':
            return {
                title: c('Partner list refusal').t`This list is archived.`,
                guidance: c('Partner list refusal')
                    .t`An archived list is read by nobody, so neither its composition nor its display mode changes any more. Nothing unarchives a list: declare a new one if the theme is needed again. Its old name stays reserved, so the new one takes a different name.`,
            };
        case 'PARTNER_LIST_HAS_HOLDERS':
            return {
                title: c('Partner list refusal').t`This list is still attached to somebody.`,
                guidance: c('Partner list refusal')
                    .t`Detaching is done from each person's own sheet, never from here. Somebody who has left the company still counts as a holder until they are detached.`,
            };
        case 'PARTNER_LIST_EMPTY_SELECTION':
            return {
                title: c('Partner list refusal').t`No partner was selected.`,
                guidance: c('Partner list refusal')
                    .t`A list is never declared empty: pick at least one partner from the eligible referential.`,
            };
        /**
         * Attention, THIS ONE IS NOT AN OPERATOR MISTAKE, and the wording must not read
         * like one. The modal offers only eligible partners; eligibility is recomputed at
         * every read, so a partner can be struck off between the moment the list was
         * displayed and the moment it was sent.
         */
        case 'PARTNER_NOT_ELIGIBLE':
            return {
                title: c('Partner list refusal').t`One of the chosen partners is no longer eligible.`,
                guidance: c('Partner list refusal')
                    .t`Eligibility is recomputed at every read: a partner struck off the network, or flagged « contacter le Channel », leaves the referential on its own and can no longer be added. Nothing was written. Reopen the list of eligible partners to see it as it is now.`,
            };
        case 'INVALID_LIMIT':
        case 'INVALID_OFFSET':
            return {
                title: c('Partner list refusal').t`The screen asked for an impossible page.`,
                guidance: c('Partner list refusal')
                    .t`Reload the page. If it happens again it is a defect of this screen and not something you can unblock.`,
            };
        default:
            return null;
    }
};

/**
 * The refusal, told to the operator who has to unblock it.
 *
 * The unknown case keeps the server's sentence: an unreadable code with no detail leaves
 * the operator with nothing to report.
 */
export const partnerListRefusalOf = (error: unknown): Refusal => {
    const known = refusalFor(refusalCode(error));
    if (known) {
        return known;
    }
    return {
        title: c('Partner list refusal').t`The server refused this change.`,
        guidance: c('Partner list refusal')
            .t`This refusal is not one the screen knows how to read, so nothing here says what to do about it. The server's answer is below — it is what the support desk needs.`,
        detail: error instanceof Error ? error.message : String(error),
    };
};
